import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { requests, send } from "../api/auth";
import { LocaleProvider } from "../i18n/LocaleProvider";
import { resolveLocale, translate, translateLabel, type Locale } from "../i18n/translate";
import { browserStore, forgetAll } from "../session/lastKnown";
import { useSession } from "../session/SessionProvider";
import { clock } from "../system/format";
import { CommandPalette } from "./CommandPalette";
import { ConsoleCrumbs, ConsoleMenu } from "./ConsoleMenu";
import {
  CONSOLE_HEADER_ITEMS,
  navigationFor,
  SURFACE_PREFIX,
  type NavItem,
  type Surface,
} from "../system/navigation";

// ---------------------------------------------------------------------------
// The application chrome. One shell, three surfaces.
//
// The surfaces differ by what the session may reach and by density, never by
// codebase (04-architecture §5). Three near-identical shells would drift within
// a slice or two, and the first thing to drift would be the sign-out control —
// which is the one control that has to work identically everywhere.
//
// THE NAVIGATION IS FILTERED BY PERMISSION AND THAT IS COURTESY, NOT SECURITY.
// A hidden link is a link somebody can still type. The API refuses the request
// and the database refuses the row; this only avoids offering a door that will
// be shut in someone's face.
// ---------------------------------------------------------------------------

const PLATFORM_DESK: NavItem = {
  id: "console.platform-reviews",
  label: "Identity reviews",
  path: "/console/platform/reviews",
  surface: "console",
  requires: "verification.review_platform",
};

export function Shell({ surface, children }: { surface: Surface; children: React.ReactNode }) {
  const { me, apiBase, reload, offlineAsOf } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const permissions = me?.authenticated ? me.permissions : [];
  // S3. Verbunk staff hold no tenant permission, so the shared menu shows them nothing
  // and their one desk had no way in. It stays out of NAV_ITEMS, whose every entry
  // must be reachable by a role holding org.read (verify-V5 §7).
  const items =
    surface === "console" && permissions.includes("verification.review_platform")
      ? [...navigationFor(surface, permissions), PLATFORM_DESK]
      : navigationFor(surface, permissions);
  const org = me?.authenticated ? me.active_org : null;
  // S145. Every surface reads the person's own choice, then their organisation's country,
  // then English: the same order as app.reader_locale(), so the database's words (0273)
  // and the screen's agree. The console used the own choice only while it was being
  // translated screen by screen; every screen now is (Run 85).
  const locale = !me?.authenticated ? "en" : resolveLocale(me.locale, org?.country);

  const [elsewhereFailed, setElsewhereFailed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (surface !== "console") return;
    const key = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearching((v) => !v);
      }
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [surface]);

  async function signOut(everywhere = false) {
    // S18. The device forgets what it last knew first, so the reload below cannot fall
    // back to it when the network is down, and a shared phone keeps nothing of this person.
    forgetAll(browserStore());
    // Best effort at the server, unconditional locally. If the network is down
    // the person still wanted to be signed out of this device, and leaving them
    // looking at a console because a request failed is the wrong answer on a
    // shared laptop in a production office.
    try {
      // Sweep S21a. Everywhere ends every session this person holds — the phone
      // left in a van, the office PC — and this one with them.
      // NOT best effort, unlike this device alone: somebody who asked for the
      // lost phone to be shut out must not be told it was when it was not.
      const { status } = await send(everywhere ? requests.logoutAll(apiBase) : requests.logout(apiBase));
      if (everywhere && status !== 200) {
        setElsewhereFailed(true);
        return;
      }
    } catch {
      if (everywhere) {
        setElsewhereFailed(true);
        return;
      }
      /* the reload below settles the truth either way */
    }
    await reload();
    navigate("/sign-in", { replace: true });
  }

  return (
    <LocaleProvider locale={locale}>
    <div className={`shell shell-${surface}`}>
      <a className="skip-link" href="#main">
        {translate(locale, "Skip to content")}
      </a>

      <header className="shell-header">
        {surface === "console" ? (
          <button type="button" className="btn btn-quiet console-menu-toggle" aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)}>
            {translate(locale, "Menu")}
          </button>
        ) : null}
        <a className="shell-mark" href={SURFACE_PREFIX[surface]}>
          Verbunk<span className="dot">.</span>
        </a>

        {org ? (
          <span className="shell-context">
            <span className="shell-org">{org.name}</span>
            {/* The role's WORDED label comes from the server (org_role.label,
                Tour T-12). Nothing here maps a role id to a word, because that
                mapping would be a list of role names living in the browser. */}
            {org.role ? <span className="shell-role">{org.role_label ? translateLabel(locale, org.role_label) : org.role}</span> : null}
          </span>
        ) : null}

        <div className="shell-header-end">
          {surface === "console" ? (
            <button type="button" className="btn btn-quiet console-search" onClick={() => setSearching(true)}>
              {translate(locale, "Search")}
              <kbd>⌘K</kbd>
            </button>
          ) : null}
          {surface === "console"
            ? items
                .filter(
                  (i) =>
                    ((CONSOLE_HEADER_ITEMS as readonly string[]).includes(i.id) && i.id !== "console.account") ||
                    i.id === PLATFORM_DESK.id,
                )
                .map((i) => (
                  <NavLink key={i.id} className="btn btn-quiet" to={i.path} end>
                    {translateLabel(locale, i.label)}
                  </NavLink>
                ))
            : null}
          <AccountMenu
            locale={locale}
            canSwitch={me?.authenticated === true && me.memberships.length > 1}
            hasAccountPage={surface === "console"}
            onSignOut={() => void signOut()}
          />
          {elsewhereFailed ? (
            <span className="muted" role="status">
              {translate(locale, "Your other devices are still signed in. Try again.")}
            </span>
          ) : null}
        </div>
      </header>

      {/* S18. The worker surface opened with no signal: say so, and say how old it is. */}
      {offlineAsOf !== null ? (
        <p className="form-message" role="status">
          {translate(locale, "Offline — showing what we last knew at {time}. Anything you record is kept on this phone and sent when there is signal.", {
            time: clock(offlineAsOf),
          })}
        </p>
      ) : null}

      <div className="shell-body">
        {surface === "console" ? (
          <ConsoleMenu items={items} pathname={pathname} permissions={permissions} locale={locale} apiBase={apiBase} open={menuOpen} />
        ) : (
        <nav
          className="shell-nav"
          aria-label={
            surface === "worker"
              ? translate(locale, "worker navigation")
              : surface === "portal"
                ? translate(locale, "portal navigation")
                : `${surface} navigation`
          }
        >
          {items.map((item) => (
            // `end` matters: without it the overview link, whose path is the
            // surface prefix, is marked current on every page underneath it.
            <NavLink key={item.id} to={item.path} end>
              {translateLabel(locale, item.label)}
            </NavLink>
          ))}
        </nav>
        )}

        <main className="shell-main" id="main" tabIndex={-1}>
          <div className="wrap">
            {surface === "console" ? <ConsoleCrumbs items={items} pathname={pathname} permissions={permissions} locale={locale} apiBase={apiBase} /> : null}
            {children}
          </div>
        </main>
      </div>
    {surface === "console" ? (
      <CommandPalette items={items} pathname={pathname} permissions={permissions} locale={locale} open={searching} onClose={() => setSearching(false)} />
    ) : null}
    </div>
    </LocaleProvider>
  );
}

/**
 * Tour T-22 (owner, 21 October 2026). One icon in place of "Switch", "Sign out" and
 * "Sign out everywhere": a small pop-up with the three things a person does with
 * their session. Closes on Escape, on a click elsewhere and after a choice. The
 * button's name is its accessible label; the icon alone is never the only signal.
 */
function AccountMenu({
  locale,
  canSwitch,
  hasAccountPage,
  onSignOut,
}: {
  locale: Locale;
  canSwitch: boolean;
  hasAccountPage: boolean;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  const label = translate(locale, "Account");
  return (
    <div className="account-menu" ref={root}>
      <button
        type="button"
        className="btn btn-quiet account-menu-button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <circle cx="9" cy="6" r="3" />
          <path d="M3 15.5c.8-3 3.3-4.5 6-4.5s5.2 1.5 6 4.5" />
        </svg>
      </button>
      {open ? (
        <div className="account-menu-pop" role="menu" onClick={() => setOpen(false)}>
          {hasAccountPage ? (
            <NavLink role="menuitem" to="/console/account">
              {translate(locale, "Your account")}
            </NavLink>
          ) : null}
          {canSwitch ? (
            <NavLink role="menuitem" to="/choose-organisation">
              {translate(locale, "Switch")}
            </NavLink>
          ) : null}
          <button role="menuitem" type="button" onClick={onSignOut}>
            {translate(locale, "Sign out")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
